// src/Views/Admin/AdminLayout.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore'; 
import { db } from '../../firebase';
import './Admin.css';

const AdminLayout = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    
    // State to track if we have verified the DB permission
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isChecking, setIsChecking] = useState(true);

    // 1. Check Database Permissions
    useEffect(() => {
        const checkPermission = async () => {
            if (!user) {
                setIsChecking(false);
                return;
            }

            try {
                // Fetch the user's profile from Firestore to see the 'isAdmin' flag
                const userRef = doc(db, 'users', user.uid);
                const snapshot = await getDoc(userRef);

                if (snapshot.exists() && snapshot.data().isAdmin === true) {
                    setIsAuthorized(true);
                } else {
                    setIsAuthorized(false);
                }
            } catch (err) {
                console.error("Admin check failed:", err);
                setIsAuthorized(false);
            } finally {
                setIsChecking(false);
            }
        };

        if (!authLoading) {
            checkPermission();
        }
    }, [user, authLoading]);

    // 2. Handle Loading States
    if (authLoading || isChecking) {
        return (
            <div className="admin-loading">
                <Icon icon="svg-spinners:90-ring-with-bg" className="admin-spinner" />
                <p>Verifying Security Clearance...</p>
            </div>
        );
    }

    // 3. Handle Unauthorized (Bounce them)
    if (!user || !isAuthorized) {
        return (
            <div className="admin-denied">
                <div className="admin-denied-card">
                    <Icon icon="solar:shield-warning-bold" className="denied-icon" />
                    <h1>Access Denied</h1>
                    <p>This workspace is restricted to AiPredict Administrators.</p>
                    <button onClick={() => navigate('/')}>Return to Base</button>
                </div>
            </div>
        );
    }

    // 4. Render The Admin Workspace
    return (
        <div className="admin-layout">
            {/* Sidebar */}
            <aside className="admin-sidebar">
                <div className="admin-brand">
                    <Icon icon="solar:cpu-bolt-bold" />
                    <span>Admin<span className="text-blue">OS</span></span>
                </div>

                <nav className="admin-nav">
                    <p className="admin-nav-label">Core</p>
                    <button 
                        className={`admin-nav-item ${location.pathname === '/admin' ? 'active' : ''}`}
                        onClick={() => navigate('/admin')}
                    >
                        <Icon icon="solar:chart-square-linear" />
                        Dashboard
                    </button>

                    <p className="admin-nav-label">Modules</p>
                    <button 
                        className={`admin-nav-item ${location.pathname === '/admin/football' ? 'active' : ''}`}
                        onClick={() => navigate('/admin/football')}
                    >
                        <Icon icon="solar:soccer-linear" />
                        Football Manager
                    </button>
                    <button 
                        className={`admin-nav-item ${location.pathname === '/admin/crypto' ? 'active' : ''}`}
                        onClick={() => navigate('/admin/crypto')}
                    >
                        <Icon icon="solar:bitcoin-coin-linear" />
                        Crypto Signals
                    </button>

                    <p className="admin-nav-label">Communication</p>
                    <button 
                        className={`admin-nav-item ${location.pathname === '/admin/announcements' ? 'active' : ''}`}
                        onClick={() => navigate('/admin/announcements')}
                    >
                        <Icon icon="solar:satellite-linear" />
                        Broadcast Center
                    </button>
                    
                    <button 
                        className={`admin-nav-item ${location.pathname === '/admin/testimonials' ? 'active' : ''}`}
                        onClick={() => navigate('/admin/testimonials')}
                    >
                        <Icon icon="solar:chat-square-like-linear" />
                        Testimonials HQ
                    </button>

                    {/* ✅ NEW SUPPORT HQ BUTTON */}
                    <button 
                        className={`admin-nav-item ${location.pathname === '/admin/support' ? 'active' : ''}`}
                        onClick={() => navigate('/admin/support')}
                    >
                        <Icon icon="solar:headset-help-linear" />
                        Support HQ
                    </button>

                </nav>

                <div className="admin-user">
                    <div className="admin-avatar">
                        {user.displayName ? user.displayName[0] : 'A'}
                    </div>
                    <div className="admin-user-info">
                        <span className="name">{user.displayName || 'Admin'}</span>
                        <span className="role">Super User</span>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="admin-content">
                {children}
            </main>
        </div>
    );
};

export default AdminLayout;